/**
 * AI Assistant Handlers
 */

import { AIAssistantState, AIChatMessage } from './state';
import { AIAssistantCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<AIAssistantCommand, { type: T }>;

// -------- Message handlers ----------

const handleAddUser = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/addUser'>
): AIAssistantState => ({
  ...state,
  messages: [
    ...state.messages,
    {
      id: cmd.payload.id,
      sender: 'user',
      content: cmd.payload.content,
      timestamp: cmd.payload.timestamp
    }
  ],
  // Any nudge `panel/open` set is scoped to the turn it was meant for — the
  // seller's first message closes that window (TV1-2385 H-2).
  inputPlaceholderKey: null
});

/**
 * Append a finished assistant message — no loading state, optionally carrying a
 * rich block. Used to seed a conversation locally (onboarding's welcome turn)
 * rather than to represent a streamed backend turn.
 */
const handleAddAssistant = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/addAssistant'>
): AIAssistantState => ({
  ...state,
  messages: [
    ...state.messages,
    {
      id: cmd.payload.id,
      sender: 'assistant',
      content: cmd.payload.content,
      timestamp: cmd.payload.timestamp,
      ...( cmd.payload.block ? { block: cmd.payload.block } : {})
    }
  ]
});

const handleAddAssistantLoading = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/addAssistantLoading'>
): AIAssistantState => ({
  ...state,
  messages: [
    ...state.messages,
    {
      id: cmd.payload.id,
      sender: 'assistant',
      content: '',
      timestamp: cmd.payload.timestamp,
      isLoading: true
    }
  ]
});

const handleSetAssistantResponse = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/setAssistantResponse'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? {
        ...msg,
        content: cmd.payload.content,
        isLoading: false,
        commandPlan: cmd.payload.commandPlan,
        planStatus: cmd.payload.commandPlan ? 'pending' as const : undefined,
        executionResults: cmd.payload.commandPlan
          ? cmd.payload.commandPlan.steps.map( step => ({
            stepId: step.id,
            status: 'pending' as const
          }))
          : undefined
      }
      : msg )
});

const handleAppendChunk = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/appendChunk'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? { ...msg, content: msg.content + cmd.payload.content }
      : msg )
});

const handleSetReportPreview = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/setReportPreview'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? { ...msg, reportPreview: cmd.payload.preview }
      : msg )
});

const handleSetBlock = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/setBlock'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? { ...msg, block: cmd.payload.block }
      : msg )
});

const handleSetLastComposedGraph = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/composer/setLastComposedGraph'>
): AIAssistantState => ({
  ...state,
  lastComposedGraph: cmd.payload.graph
});

const handleSettleLoading = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/settleLoading'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.sender === 'assistant' && msg.isLoading
      ? {
        ...msg,
        isLoading: false,
        // Keep whatever streamed in before the abort. Only a bubble that never
        // received anything gets the fallback, so an interrupted answer is not
        // thrown away.
        content: msg.content || cmd.payload.fallbackContent || ''
      }
      : msg )
});

/**
 * Attach the run's trace id to one bubble. Targeted by id — never "the last assistant
 * message" — because the `done` update can land after a fast-path plan or an aborted
 * turn already appended another bubble.
 */
const handleSetTraceId = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/message/setTraceId'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId ? { ...msg, traceId: cmd.payload.traceId } : msg )
});

/**
 * Record the seller's rating OPTIMISTICALLY — see `AIChatMessage.feedback`. The
 * forwarding policy never dispatches a rollback, on purpose: a thumbs-down that
 * visually undoes itself because an observability sink is unconfigured would read as
 * the app rejecting her opinion.
 */
const handleFeedbackSubmit = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/feedback/submit'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId ? { ...msg, feedback: cmd.payload.rating } : msg )
});

const handleClearAll = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/message/clearAll'>
): AIAssistantState => ({
  ...state,
  messages: [],
  executingPlanMessageId: null,
  currentExecutingStepIndex: 0
});

// -------- Processing handlers ----------

const handleSetProcessing = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/processing/setProcessing'>
): AIAssistantState => ({
  ...state,
  isProcessing: cmd.payload.isProcessing
});

const handleSetStep = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/processing/setStep'>
): AIAssistantState => ({
  ...state,
  processingStep: cmd.payload.step
});

// -------- Plan execution handlers ----------

const handleStartExecution = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/plan/startExecution'>
): AIAssistantState => ({
  ...state,
  executingPlanMessageId: cmd.payload.messageId,
  currentExecutingStepIndex: 0,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? { ...msg, planStatus: 'executing' as const }
      : msg )
});

const handleStepStarted = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/plan/stepStarted'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg => {
    if ( msg.id !== state.executingPlanMessageId ) return msg;
    return {
      ...msg,
      executionResults: msg.executionResults?.map( r =>
        r.stepId === cmd.payload.stepId ? cmd.payload.result : r )
    };
  })
});

const handleStepCompleted = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/plan/stepCompleted'>
): AIAssistantState => {
  const updatedMessages = state.messages.map( msg => {
    if ( msg.id !== state.executingPlanMessageId ) return msg;
    return {
      ...msg,
      executionResults: msg.executionResults?.map( r =>
        r.stepId === cmd.payload.stepId ? cmd.payload.result : r )
    };
  });
  return {
    ...state,
    messages: updatedMessages,
    currentExecutingStepIndex: state.currentExecutingStepIndex + 1
  };
};

const handlePlanCompleted = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/plan/completed'>
): AIAssistantState => ({
  ...state,
  executingPlanMessageId: null,
  currentExecutingStepIndex: 0,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? { ...msg, planStatus: 'completed' as const }
      : msg )
});

const handlePlanAborted = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/plan/aborted'>
): AIAssistantState => {
  const updatedMessages = state.messages.map( msg => {
    if ( msg.id !== cmd.payload.messageId ) return msg;
    return {
      ...msg,
      planStatus: 'aborted' as const,
      executionResults: msg.executionResults?.map( r =>
        r.status === 'pending' ? { ...r, status: 'skipped' as const } : r )
    };
  });
  return {
    ...state,
    executingPlanMessageId: null,
    currentExecutingStepIndex: 0,
    messages: updatedMessages
  };
};

const handlePlanDiscarded = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/plan/discarded'>
): AIAssistantState => ({
  ...state,
  messages: state.messages.map( msg =>
    msg.id === cmd.payload.messageId
      ? { ...msg, planStatus: 'discarded' as const }
      : msg )
});

const handleHumanStepCompleted = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/plan/humanStepCompleted'>
): AIAssistantState => {
  if ( !state.executingPlanMessageId ) return state;

  const message = state.messages.find( m => m.id === state.executingPlanMessageId );
  if ( !message?.commandPlan ) return state;

  const step = message.commandPlan.steps[state.currentExecutingStepIndex];
  if ( !step || step.storeName !== '__human' ) return state;

  const updatedMessages = state.messages.map( msg => {
    if ( msg.id !== state.executingPlanMessageId ) return msg;
    return {
      ...msg,
      executionResults: msg.executionResults?.map( r =>
        r.stepId === step.id ? { ...r, status: 'completed' as const } : r )
    };
  });

  return {
    ...state,
    messages: updatedMessages,
    currentExecutingStepIndex: state.currentExecutingStepIndex + 1
  };
};

// -------- Phase handlers ----------

const handleSetPhase = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/processing/setPhase'>
): AIAssistantState => ({
  ...state,
  processingPhase: cmd.payload.phase
});

// -------- Error handlers ----------

const handleSetStepError = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/error/setStepError'>
): AIAssistantState => ({
  ...state,
  stepError: cmd.payload.error
});

const handleSetPlanValidationError = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/error/setPlanValidationError'>
): AIAssistantState => ({
  ...state,
  planValidationError: cmd.payload.error
});

const handleClearErrors = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/error/clear'>
): AIAssistantState => ({
  ...state,
  stepError: null,
  planValidationError: null
});

// -------- Panel handlers ----------

const handleTogglePanel = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/panel/toggle'>
): AIAssistantState => {
  const isPanelOpen = !state.isPanelOpen;
  return {
    ...state,
    isPanelOpen,
    // A closing toggle ends whatever nudge was showing (TV1-2385 H-2). An
    // opening toggle (navbar, panel's own close button) never sets one, so
    // leaving it untouched there is a no-op.
    inputPlaceholderKey: isPanelOpen ? state.inputPlaceholderKey : null
  };
};

/**
 * Open the panel — IDEMPOTENT (unlike `toggle`, see `PanelCommands`).
 *
 * - No `placeholderKey`: just opens. Never touches the conversation or the
 *   placeholder — a caller that doesn't ask for a fresh start doesn't get one.
 * - `placeholderKey` present, conversation EMPTY: nothing to displace, applies
 *   immediately.
 * - `placeholderKey` present, conversation LIVE (`messages.length > 0`): does
 *   NOT open. Raises `pendingPanelOpenConfirmation` instead — the caller must
 *   resolve it via `panel/confirmOpen` or `panel/cancelOpen` — because the
 *   conversation-history surface cannot restore a displaced conversation
 *   (TV1-2385 H-5).
 */
const handleOpenPanel = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/panel/open'>
): AIAssistantState => {
  const { placeholderKey } = cmd.payload;

  if ( !placeholderKey ) {
    return { ...state, isPanelOpen: true };
  }

  const hasLiveConversation = state.messages.length > 0;
  if ( hasLiveConversation ) {
    return { ...state, pendingPanelOpenConfirmation: { placeholderKey }};
  }

  return { ...state, isPanelOpen: true, inputPlaceholderKey: placeholderKey };
};

/**
 * Resolve a pending `panel/open` confirmation by PROCEEDING — resets the
 * conversation (mirrors `handleNewConversation`'s clearing, minus the history
 * push: the whole reason this confirmation exists is that history push does
 * not make the conversation recoverable) and opens with the placeholder.
 */
const handleConfirmOpenPanel = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/panel/confirmOpen'>
): AIAssistantState => {
  if ( !state.pendingPanelOpenConfirmation ) return state;

  return {
    ...state,
    isPanelOpen: true,
    inputPlaceholderKey: state.pendingPanelOpenConfirmation.placeholderKey,
    pendingPanelOpenConfirmation: null,
    conversationId: null,
    messages: [],
    executingPlanMessageId: null,
    currentExecutingStepIndex: 0
  };
};

/**
 * Resolve a pending `panel/open` confirmation by CANCELING — opens on the
 * EXISTING conversation, untouched, and does not set the placeholder (it was
 * never meant for this conversation).
 */
const handleCancelOpenPanel = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/panel/cancelOpen'>
): AIAssistantState => {
  if ( !state.pendingPanelOpenConfirmation ) return state;

  return { ...state, isPanelOpen: true, pendingPanelOpenConfirmation: null };
};

const handleSetPanelMode = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/panel/setMode'>
): AIAssistantState => ({
  ...state,
  panelMode: cmd.payload.mode
});

// -------- Settings handlers ----------

const handleToggleAutoExecute = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/settings/toggleAutoExecute'>
): AIAssistantState => ({
  ...state,
  autoExecute: !state.autoExecute
});

// -------- Conversation handlers ----------

const handleSetConversationId = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/conversation/setId'>
): AIAssistantState => ({
  ...state,
  conversationId: cmd.payload.id
});

const handleSetHistory = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/conversation/setHistory'>
): AIAssistantState => ({
  ...state,
  conversationHistory: cmd.payload.conversations,
  isLoadingHistory: false
});

const handleSetLoadingHistory = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/conversation/setLoadingHistory'>
): AIAssistantState => ({
  ...state,
  isLoadingHistory: cmd.payload.isLoading
});

const handleNewConversation = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/conversation/new'>
): AIAssistantState => {
  // Save current conversation to history before clearing (if messages exist)
  let conversationHistory = state.conversationHistory;
  if ( state.messages.length > 0 ) {
    const firstUserMessage = state.messages.find( m => m.sender === 'user' );
    const summary: import( '@infrastructure/aiAssistant/types' ).ConversationSummary = {
      id: state.conversationId || `conv-${Date.now()}-${Math.random().toString( 36 )
.slice( 2, 8 )}`,
      title: firstUserMessage?.content.slice( 0, 80 ) || 'Untitled conversation',
      lastMessage: state.messages[state.messages.length - 1]?.content.slice( 0, 120 ) || '',
      updatedAt: Date.now(),
      messageCount: state.messages.length
    };
    conversationHistory = [ summary, ...state.conversationHistory ];
  }

  return {
    ...state,
    conversationId: null,
    messages: [],
    executingPlanMessageId: null,
    currentExecutingStepIndex: 0,
    conversationHistory
  };
};

// -------- Approval handlers ----------

const handleApprovalSetPending = (
  state: AIAssistantState,
  cmd: ExtractCommand<'aiAssistant/approval/setPending'>
): AIAssistantState => ({
  ...state,
  pendingApproval: {
    toolName: cmd.payload.toolName,
    humanizedName: cmd.payload.humanizedName,
    args: cmd.payload.args,
    conversationId: cmd.payload.conversationId
  },
  pendingApprovalDecision: null
});

const handleApprovalApprove = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/approval/approve'>
): AIAssistantState => {
  // Only transition if there is a pending approval; otherwise no-op.
  if ( !state.pendingApproval ) return state;
  return {
    ...state,
    pendingApprovalDecision: 'approved'
  };
};

const handleApprovalReject = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/approval/reject'>
): AIAssistantState => {
  if ( !state.pendingApproval ) return state;
  return {
    ...state,
    pendingApprovalDecision: 'rejected'
  };
};

const handleApprovalClear = (
  state: AIAssistantState,
  _cmd: ExtractCommand<'aiAssistant/approval/clear'>
): AIAssistantState => ({
  ...state,
  pendingApproval: null,
  pendingApprovalDecision: null
});

// -------- Handler map ----------

export const handlers: CommandHandlerMap<AIAssistantState, AIAssistantCommand> = {
  // Messages
  'aiAssistant/message/addUser': handleAddUser,
  'aiAssistant/message/addAssistant': handleAddAssistant,
  'aiAssistant/message/addAssistantLoading': handleAddAssistantLoading,
  'aiAssistant/message/settleLoading': handleSettleLoading,
  'aiAssistant/message/setTraceId': handleSetTraceId,
  'aiAssistant/feedback/submit': handleFeedbackSubmit,
  'aiAssistant/message/setAssistantResponse': handleSetAssistantResponse,
  'aiAssistant/message/appendChunk': handleAppendChunk,
  'aiAssistant/message/setReportPreview': handleSetReportPreview,
  'aiAssistant/message/setBlock': handleSetBlock,
  // Rule composer (TV1-2340 · P5)
  'aiAssistant/composer/setLastComposedGraph': handleSetLastComposedGraph,
  'aiAssistant/message/clearAll': handleClearAll,
  // Processing
  'aiAssistant/processing/setProcessing': handleSetProcessing,
  'aiAssistant/processing/setStep': handleSetStep,
  'aiAssistant/processing/setPhase': handleSetPhase,
  // Plan execution
  'aiAssistant/plan/startExecution': handleStartExecution,
  'aiAssistant/plan/stepStarted': handleStepStarted,
  'aiAssistant/plan/stepCompleted': handleStepCompleted,
  'aiAssistant/plan/completed': handlePlanCompleted,
  'aiAssistant/plan/aborted': handlePlanAborted,
  'aiAssistant/plan/discarded': handlePlanDiscarded,
  'aiAssistant/plan/humanStepCompleted': handleHumanStepCompleted,
  // Errors
  'aiAssistant/error/setStepError': handleSetStepError,
  'aiAssistant/error/setPlanValidationError': handleSetPlanValidationError,
  'aiAssistant/error/clear': handleClearErrors,
  // Panel
  'aiAssistant/panel/toggle': handleTogglePanel,
  'aiAssistant/panel/open': handleOpenPanel,
  'aiAssistant/panel/confirmOpen': handleConfirmOpenPanel,
  'aiAssistant/panel/cancelOpen': handleCancelOpenPanel,
  'aiAssistant/panel/setMode': handleSetPanelMode,
  // Settings
  'aiAssistant/settings/toggleAutoExecute': handleToggleAutoExecute,
  // Conversation
  'aiAssistant/conversation/setId': handleSetConversationId,
  'aiAssistant/conversation/setHistory': handleSetHistory,
  'aiAssistant/conversation/setLoadingHistory': handleSetLoadingHistory,
  'aiAssistant/conversation/new': handleNewConversation,
  // Approval
  'aiAssistant/approval/setPending': handleApprovalSetPending,
  'aiAssistant/approval/approve': handleApprovalApprove,
  'aiAssistant/approval/reject': handleApprovalReject,
  'aiAssistant/approval/clear': handleApprovalClear
};
